import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Box, 
  Chip, 
  Grid,
  CircularProgress,
  Divider,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import LoadErrorScreen from './LoadErrorScreen';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [tourPaymentDialogOpen, setTourPaymentDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [paymentComment, setPaymentComment] = useState('');
  const [tourPaymentComment, setTourPaymentComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const cancelTimersRef = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();

    // Периодическое обновление списка заказов (без перезагрузки страницы)
    const POLL_INTERVAL_MS = 25000; // 25 секунд
    const pollTimer = setInterval(() => {
      fetchOrders(true); // silent — без индикатора загрузки
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollTimer);
      Object.values(cancelTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  // Устанавливаем таймеры автоматической отмены для заказов в статусе waiting_payment
  useEffect(() => {
    // Очищаем предыдущие таймеры только для заказов, которых больше нет в списке или статус изменился
    Object.keys(cancelTimersRef.current).forEach(orderId => {
      const order = orders.find(o => o._id === orderId);
      if (!order || order.status !== 'waiting_payment') {
        // Заказ удален или статус изменился - очищаем таймер
        if (cancelTimersRef.current[orderId]) {
          clearTimeout(cancelTimersRef.current[orderId]);
          delete cancelTimersRef.current[orderId];
        }
      }
    });

    // Устанавливаем таймеры для новых заказов в статусе waiting_payment
    orders.forEach(order => {
      // Пропускаем заказы, для которых таймер уже установлен
      if (cancelTimersRef.current[order._id]) {
        return;
      }

      if (order.status === 'waiting_payment' && order.createdAt) {
        const createdAt = new Date(order.createdAt);
        const cancelTime = new Date(createdAt.getTime() + 20 * 60 * 1000); // +20 минут
        const now = new Date();
        const timeLeft = cancelTime - now;

        if (timeLeft > 0) {
          const timer = setTimeout(() => {
            handleAutoCancel(order._id);
          }, timeLeft);
          cancelTimersRef.current[order._id] = timer;
        } else {
          // Уже прошло 20 минут, отменяем сразу
          handleAutoCancel(order._id);
        }
      }
    });
  }, [orders]);

  const fetchOrders = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setLoadError(false);
      setError('');
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const [ordersRes, productOrdersRes, tourBookingsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders/my`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_SERVER_URL}/api/product-orders`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_SERVER_URL}/api/tour-bookings/my`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!ordersRes.ok) {
        if (ordersRes.status === 401) {
          localStorage.removeItem('token');
          navigate('/');
          return;
        }
        if (!silent) throw new Error('Ошибка загрузки заказов');
        return;
      }

      const ordersData = await ordersRes.json();
      const rentalList = Array.isArray(ordersData) ? ordersData : [];
      const productList = productOrdersRes.ok ? (await productOrdersRes.json()) : [];
      const rentalWithType = rentalList.map((o) => ({ ...o, orderType: 'rental' }));
      const shopWithType = (Array.isArray(productList) ? productList : []).map((o) => ({
        ...o,
        orderType: 'shop',
        _id: o._id,
        shortId: o.shortId,
        createdAt: o.createdAt,
        status: o.status,
      }));
      const tourListRaw = tourBookingsRes.ok ? await tourBookingsRes.json().catch(() => []) : [];
      const tourWithType = (Array.isArray(tourListRaw) ? tourListRaw : []).map((b) => ({
        ...b,
        orderType: 'tour',
      }));
      const merged = [...rentalWithType, ...shopWithType, ...tourWithType].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(merged);
    } catch (err) {
      if (!silent) {
        console.error('Ошибка загрузки заказов:', err);
        setLoadError(true);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getStatusLabel = (status, orderType) => {
    if (orderType === 'tour') {
      const tourLabels = {
        awaiting_prepayment: 'Ожидает предоплаты',
        awaiting_prepayment_confirmation: 'Ожидает подтверждения предоплаты',
        cancelled: 'Отменено',
        completed: 'Завершено',
      };
      return tourLabels[status] || status;
    }
    if (orderType === 'shop') {
      const shopLabels = { pending: 'Ожидает выдачи', received: 'Получено', cancelled: 'Отменено' };
      return shopLabels[status] || status;
    }
    const statusLabels = {
      waiting_payment: 'Ожидание оплаты',
      waiting_confirmation: 'Ожидание подтверждения',
      reserved: 'Забронировано',
      awaiting_start: 'Ожидание начала',
      active: 'Активна',
      completed: 'Завершено',
      overdue: 'Просрочено',
      cancelled: 'Отменено',
      expired: 'Истекло',
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status, orderType) => {
    if (orderType === 'tour') {
      const tourColors = {
        awaiting_prepayment: 'warning',
        awaiting_prepayment_confirmation: 'info',
        cancelled: 'error',
        completed: 'success',
      };
      return tourColors[status] || 'default';
    }
    if (orderType === 'shop') {
      const shopColors = { pending: 'warning', received: 'success', cancelled: 'error' };
      return shopColors[status] || 'default';
    }
    const statusColors = {
      waiting_payment: 'warning',
      waiting_confirmation: 'info',
      reserved: 'primary',
      awaiting_start: 'info',
      active: 'success',
      completed: 'default',
      overdue: 'error',
      cancelled: 'error',
      expired: 'default',
    };
    return statusColors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      const trimmed = photo.trim();
      if (!trimmed) return null;
      
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      if (trimmed.startsWith('/')) {
        return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
      }
      return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
    }
    return null;
  };

  // Функция для расчета залога из depositBreakdown (как в ItemPage)
  const getDepositValue = (order) => {
    if (!order) return 0;
    
    // Приоритет: depositBreakdown (суммируем subtotal всех позиций)
    if (order.depositBreakdown && Array.isArray(order.depositBreakdown) && order.depositBreakdown.length > 0) {
      const total = order.depositBreakdown.reduce((sum, item) => {
        // Проверяем оба варианта: subtotal и расчет из perUnit * quantity
        let itemDeposit = 0;
        if (item.subtotal !== undefined && item.subtotal !== null && !isNaN(Number(item.subtotal))) {
          itemDeposit = Number(item.subtotal);
        } else if (item.perUnit !== undefined && item.quantity !== undefined) {
          const perUnit = Number(item.perUnit) || 0;
          const quantity = Number(item.quantity) || 1;
          itemDeposit = perUnit * quantity;
        }
        return sum + itemDeposit;
      }, 0);
      if (total > 0) {
        return total;
      }
    }
    
    // Если depositBreakdown пуст или сумма 0, используем depositTotal
    const depositTotal = Number(order.depositTotal) || 0;
    if (depositTotal > 0) {
      return depositTotal;
    }
    
    // Если ничего не найдено, возвращаем 0
    return 0;
  };

  // Функция для расчета суммы предоплаты (стоимость за 1 день)
  const getPrepaidAmount = (order) => {
    if (!order) return 0;
    
    // Приоритет: pricing.prepaidAmount, затем payment.prepay.amount, затем расчет из items
    if (order.pricing && order.pricing.prepaidAmount) {
      return Number(order.pricing.prepaidAmount);
    }
    
    if (order.payment && order.payment.prepay && order.payment.prepay.amount) {
      return Number(order.payment.prepay.amount);
    }
    
    // Если нет в pricing, рассчитываем из items (стоимость за 1 день)
    if (order.items && Array.isArray(order.items) && order.items.length > 0) {
      return order.items.reduce((sum, item) => {
        const pricePerDay = Number(item.pricePerDay) || 0;
        const quantity = Number(item.quantity) || 1;
        return sum + (pricePerDay * quantity);
      }, 0);
    }
    
    return 0;
  };

  const getRemainingAmount = (order) => {
    if (!order) return 0;
    
    // Приоритет: pricing.remainingAmount, затем payment.final.amount, затем расчет (totalPrice - prepaidAmount)
    if (order.pricing && order.pricing.remainingAmount !== undefined) {
      return Number(order.pricing.remainingAmount);
    }
    
    if (order.payment && order.payment.final && order.payment.final.amount) {
      return Number(order.payment.final.amount);
    }
    
    // Рассчитываем: общая стоимость - предоплата
    const totalPrice = Number(order.totalPrice) || 0;
    const prepaidAmount = getPrepaidAmount(order);
    return Math.max(0, totalPrice - prepaidAmount);
  };

  // Получить время до автоматической отмены
  const getTimeUntilCancel = (order) => {
    if (order.status !== 'waiting_payment' || !order.createdAt) return null;
    
    const createdAt = new Date(order.createdAt);
    const cancelTime = new Date(createdAt.getTime() + 20 * 60 * 1000); // +20 минут
    const now = new Date();
    const timeLeft = cancelTime - now;
    
    if (timeLeft <= 0) return null;
    
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    return { minutes, seconds };
  };

  // Для тур-броней: 2 часа на внесение предоплаты
  const getTourTimeUntilCancel = (booking) => {
    if (booking?.orderType !== 'tour' || booking?.status !== 'awaiting_prepayment' || !booking?.createdAt) {
      return null;
    }
    const createdAt = new Date(booking.createdAt);
    const cancelTime = new Date(createdAt.getTime() + 2 * 60 * 60 * 1000);
    const now = new Date();
    const timeLeft = cancelTime - now;
    if (timeLeft <= 0) return null;
    const hours = Math.floor(timeLeft / (60 * 60 * 1000));
    const minutes = Math.floor((timeLeft % (60 * 60 * 1000)) / 60000);
    return { hours, minutes };
  };

  const handleAutoCancel = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Обновляем список заказов
        fetchOrders();
      }
    } catch (err) {
      console.error('Ошибка автоматической отмены заказа:', err);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrderId) return;

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders/${selectedOrderId}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка отмены заказа');
      }

      setCancelDialogOpen(false);
      setSelectedOrderId(null);
      fetchOrders(); // Обновляем список заказов
    } catch (err) {
      console.error('Ошибка отмены заказа:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayPrepayment = (order) => {
    setSelectedOrderId(order._id);
    setPaymentComment('');
    setError('');
    setPaymentDialogOpen(true);
  };

  const handleConfirmPayment = async () => {
    if (!selectedOrderId) return;

    setProcessing(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/orders/${selectedOrderId}/i-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          comment: paymentComment.trim() || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка подтверждения оплаты');
      }

      // Останавливаем таймер отмены для этого заказа
      if (cancelTimersRef.current[selectedOrderId]) {
        clearTimeout(cancelTimersRef.current[selectedOrderId]);
        delete cancelTimersRef.current[selectedOrderId];
      }

      setPaymentDialogOpen(false);
      setSelectedOrderId(null);
      setPaymentComment('');
      fetchOrders(); // Обновляем список заказов
    } catch (err) {
      console.error('Ошибка подтверждения оплаты:', err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayTourPrepayment = (booking) => {
    setSelectedOrderId(booking._id);
    setTourPaymentComment('');
    setError('');
    setTourPaymentDialogOpen(true);
  };

  const handleConfirmTourPayment = async () => {
    if (!selectedOrderId) return;

    setProcessing(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/tour-bookings/${selectedOrderId}/mark-prepaid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comment: tourPaymentComment.trim() || '' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка подтверждения оплаты');
      }

      setTourPaymentDialogOpen(false);
      setSelectedOrderId(null);
      setTourPaymentComment('');
      fetchOrders(true);
    } catch (err) {
      console.error('Ошибка подтверждения оплаты тура:', err);
      setError(err.message || 'Ошибка подтверждения оплаты');
    } finally {
      setProcessing(false);
    }
  };

  const handleRepeatOrder = async (order) => {
    if (!order || !order.items || order.items.length === 0) {
      setError('Не удалось повторить заказ: товары не найдены');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      // Добавляем все товары из заказа в корзину
      for (const orderItem of order.items) {
        const itemId = orderItem.itemId?._id || orderItem.itemId;
        const quantity = orderItem.quantity || 1;

        if (!itemId) {
          console.warn('Пропущен товар без itemId:', orderItem);
          continue;
        }

        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            itemId,
            quantity,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Ошибка добавления товара в корзину' }));
          throw new Error(errorData.message || 'Ошибка добавления товара в корзину');
        }
      }

      // Перенаправляем на страницу корзины
      navigate('/cart');
    } catch (err) {
      console.error('Ошибка повторения заказа:', err);
      setError(err.message || 'Ошибка при повторении заказа');
    } finally {
      setProcessing(false);
    }
  };

  const getItemLink = (item) => {
    // Используем inventoryNumber если есть, иначе _id
    if (item.inventoryNumber) {
      return `/item/${item.inventoryNumber}`;
    }
    if (item.itemId) {
      return `/item/${item.itemId}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (error) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ color: '#d32f2f', textAlign: 'center' }}>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4, backgroundColor: '#F5F3F0', minHeight: '100vh', py: 4 }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          color: '#000000', 
          fontWeight: 'bold',
          fontFamily: 'serif',
          mb: 4
        }}
      >
        Мои заказы
      </Typography>

      {orders.length === 0 ? (
        <Card sx={{ backgroundColor: '#FFFFFF', mt: 2 }}>
          <CardContent>
            <Typography variant="h6" align="center" sx={{ color: '#000000', py: 4 }}>
              У вас пока нет заказов и бронирований туров
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => {
            if (order.orderType === 'tour') {
              const from = order.periodSnapshot?.dateFrom;
              const to = order.periodSnapshot?.dateTo;
              const pay = order.operatorPaymentSnapshot || {};
              const tourTimeLeft = getTourTimeUntilCancel(order);
              return (
                <Grid item xs={12} key={order._id}>
                  <Card
                    sx={{
                      backgroundColor: '#FFFFFF',
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                      borderLeft: '4px solid #8B7D6B',
                      '&:hover': {
                        boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold', mb: 0.5 }}>
                            Бронирование тура
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 600 }}>
                            {order.tourTitle || 'Тур'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#757575' }}>
                            {order.tourNumber} • оформлено: {formatDateTime(order.createdAt)}
                          </Typography>
                          {(from || to) && (
                            <Typography variant="body2" sx={{ color: '#000000', mt: 1 }}>
                              Заезд: {from ? formatDate(from) : '—'} — {to ? formatDate(to) : '—'}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: '#000000', mt: 0.5 }}>
                            Мест: {order.seats ?? '—'}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusLabel(order.status, 'tour')}
                          color={getStatusColor(order.status, 'tour')}
                          sx={{ fontWeight: 'bold', flexShrink: 0 }}
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#757575' }}>
                            Предоплата
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#8B7D6B', fontWeight: 'bold' }}>
                            {Number(order.prepaymentAmount ?? 0).toLocaleString('ru-RU')} ₽
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#757575' }}>
                            Сумма тура
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold' }}>
                            {Number(order.totalPrice ?? 0).toLocaleString('ru-RU')} ₽
                          </Typography>
                        </Box>
                        <Button
                          component={Link}
                          to={`/tours/${encodeURIComponent(order.tourNumber || '')}`}
                          variant="outlined"
                          sx={{
                            ml: { xs: 0, sm: 'auto' },
                            textTransform: 'none',
                            borderColor: '#8B7D6B',
                            color: '#8B7D6B',
                            '&:hover': { borderColor: '#6B5D4B', backgroundColor: 'rgba(139, 125, 107, 0.06)' },
                          }}
                        >
                          Страница тура
                        </Button>
                      </Box>
                      {order.status === 'awaiting_prepayment' && (
                        <Box sx={{ mt: 2 }}>
                          <Alert severity={tourTimeLeft ? 'warning' : 'error'} sx={{ mb: 2 }}>
                            {tourTimeLeft
                              ? `Вам необходимо внести предоплату в течение двух часов. Осталось: ${tourTimeLeft.hours} ч ${tourTimeLeft.minutes} мин.`
                              : 'Время на внесение предоплаты истекло. Бронь будет автоматически отменена.'}
                          </Alert>

                          <Box sx={{ p: 2, backgroundColor: '#FFFFFF', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 'bold', mb: 1.5 }}>
                              Реквизиты туроператора
                            </Typography>
                            {pay.recipientFullName && (
                              <Typography variant="body2" sx={{ color: '#000000', mb: 0.75 }}>
                                <strong>Получатель:</strong> {pay.recipientFullName}
                              </Typography>
                            )}
                            {pay.banks && (
                              <Typography variant="body2" sx={{ color: '#000000', mb: 0.75 }}>
                                <strong>Банк:</strong> {pay.banks}
                              </Typography>
                            )}
                            {pay.cardNumber && (
                              <Typography variant="body2" sx={{ color: '#000000', mb: 0.75 }}>
                                <strong>Карта:</strong> {pay.cardNumber}
                              </Typography>
                            )}
                            {pay.phoneForTransfer && (
                              <Typography variant="body2" sx={{ color: '#000000', mb: 0.75 }}>
                                <strong>Телефон для перевода:</strong> {pay.phoneForTransfer}
                              </Typography>
                            )}
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                onClick={() => handlePayTourPrepayment(order)}
                                disabled={processing || !tourTimeLeft}
                                sx={{
                                  backgroundColor: '#8B7D6B',
                                  color: '#FFFFFF',
                                  fontWeight: 'bold',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: '#6B5D4B' },
                                  '&:disabled': { backgroundColor: '#8B7D6B', opacity: 0.6 },
                                }}
                              >
                                Я внес предоплату
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      {['awaiting_prepayment_confirmation', 'completed'].includes(order.status) &&
                        (() => {
                          const snap = order.operatorPaymentSnapshot || {};
                          const tel = String(snap.operatorContactPhone || snap.phoneForTransfer || '').trim();
                          if (!tel) return null;
                          return (
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: '#e8f5e9',
                                border: '1px solid rgba(46, 125, 50, 0.35)',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#000000' }}>
                                <strong>Телефон туроператора:</strong>{' '}
                                <Box
                                  component="a"
                                  href={`tel:${tel.replace(/\s/g, '')}`}
                                  sx={{ color: '#2e7d32', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {tel}
                                </Box>
                              </Typography>
                            </Box>
                          );
                        })()}
                    </CardContent>
                  </Card>
                </Grid>
              );
            }

            return (
            <Grid item xs={12} key={order._id}>
              <Card 
                sx={{ 
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', '& > *': { maxWidth: '100%' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold', mb: 1 }}>
                        {order.orderType === 'shop' ? 'Заказ магазина' : 'Заказ'} #{order.shortId || order._id?.toString?.()?.slice(-8) || '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#757575' }}>
                        Создан: {formatDateTime(order.createdAt)}
                      </Typography>
                    </Box>
                    <Chip 
                      label={getStatusLabel(order.status, order.orderType)} 
                      color={getStatusColor(order.status, order.orderType)}
                      sx={{ fontWeight: 'bold', flexShrink: 0 }}
                    />
                  </Box>

                  {order.orderType !== 'shop' && order.orderType !== 'tour' && order.pickupCode
                    && !['cancelled', 'completed', 'expired', 'overdue', 'active'].includes(order.status) && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(139, 125, 107, 0.12)',
                        border: '1px dashed rgba(139, 125, 107, 0.45)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#757575', mb: 0.5 }}>
                        Код для получения
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          color: '#000000',
                          fontWeight: 'bold',
                          fontFamily: 'monospace',
                          letterSpacing: '0.35em',
                        }}
                      >
                        {order.pickupCode}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#757575', display: 'block', mt: 0.5 }}>
                        Назовите этот код сотруднику при получении оборудования
                      </Typography>
                    </Box>
                  )}

                  {/* Заказ из магазина: товары, пункт выдачи, сумма */}
                  {order.orderType === 'shop' && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 'bold', mb: 1 }}>
                          Товары:
                        </Typography>
                        <Grid container spacing={2}>
                          {(order.items || []).map((item, index) => (
                            <Grid item xs={12} key={index}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#000000', fontWeight: 'bold' }}>
                                  {item.title}
                                  {item.productNumber && (
                                    <Typography component="span" variant="caption" sx={{ color: '#757575', ml: 1 }}>
                                      {item.productNumber}
                                    </Typography>
                                  )}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#8B7D6B' }}>
                                  {item.quantity} × {item.pricePerUnit ?? 0} ₽ = {item.subtotal ?? 0} ₽
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                      {order.branchId && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: '#757575', mb: 0.5 }}>
                            Пункт выдачи:
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#000000' }}>
                            {order.branchId.city && `${order.branchId.city}, `}
                            {order.branchId.address}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#757575' }}>
                          Сумма заказа:
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold' }}>
                          {order.totalAmount ?? 0} ₽
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#757575' }}>
                          Оплата при получении
                        </Typography>
                      </Box>
                    </>
                  )}

                  {/* Аренда: предупреждение о предоплате */}
                  {order.orderType !== 'shop' && order.status === 'waiting_payment' && (() => {
                    const timeLeft = getTimeUntilCancel(order);
                    return (
                      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, flex: '0 0 auto', mb: 2 }}>
                        {timeLeft ? (
                          <Alert
                            severity="warning"
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              py: 1,
                              '& .MuiAlert-message': { width: '100%', boxSizing: 'border-box' },
                            }}
                          >
                            ⚠️ Предоплата должна быть внесена в течение 20 минут, в противном случае заказ будет отменен.
                            <br />
                            Осталось времени: {timeLeft.minutes} мин. {timeLeft.seconds} сек.
                          </Alert>
                        ) : (
                          <Alert
                            severity="error"
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              py: 1,
                              '& .MuiAlert-message': { width: '100%', boxSizing: 'border-box' },
                            }}
                          >
                            Время на оплату истекло. Заказ будет автоматически отменен.
                          </Alert>
                        )}
                      </Box>
                    );
                  })()}

                  {order.orderType !== 'shop' && <Divider sx={{ my: 2 }} />}

                  {/* Товары в заказе (аренда) */}
                  {order.orderType !== 'shop' && order.items && order.items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ color: '#000000', fontWeight: 'bold', mb: 1 }}>
                        Товары:
                      </Typography>
                      <Grid container spacing={2}>
                        {order.items.map((item, index) => {
                          const itemLink = getItemLink(item);
                          const ItemWrapper = itemLink ? Link : Box;
                          const itemProps = itemLink ? { to: itemLink, style: { textDecoration: 'none', color: 'inherit' } } : {};
                          
                          return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <ItemWrapper {...itemProps}>
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    alignItems: 'center',
                                    cursor: itemLink ? 'pointer' : 'default',
                                    '&:hover': itemLink ? {
                                      backgroundColor: 'rgba(139, 125, 107, 0.05)',
                                      borderRadius: 1,
                                      p: 1,
                                      mt: -1,
                                      ml: -1,
                                    } : {},
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  {item.photo && (
                                    <Box
                                      component="img"
                                      src={getPhotoUrl(item.photo)}
                                      alt={item.title}
                                      sx={{
                                        width: 80,
                                        height: 80,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                      }}
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/80';
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2" sx={{ color: '#000000', fontWeight: 'bold' }}>
                                      {item.title}
                                    </Typography>
                                    {item.inventoryNumber && (
                                      <Typography variant="caption" sx={{ color: '#757575' }}>
                                        {item.inventoryNumber}
                                      </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: '#8B7D6B', mt: 0.5 }}>
                                      Кол-во: {item.quantity} × {item.pricePerDay || item.pricePerHour} ₽
                                    </Typography>
                                  </Box>
                                </Box>
                              </ItemWrapper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}

                  {order.orderType !== 'shop' && <Divider sx={{ my: 2 }} />}

                  {/* Информация о периоде аренды */}
                  {order.orderType !== 'shop' && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#757575' }}>
                        Начало аренды:
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        {formatDate(order.startDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: '#757575' }}>
                        Окончание аренды:
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        {formatDate(order.returnDate)}
                      </Typography>
                    </Grid>
                    {order.periodAmount && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: '#757575' }}>
                          Период: {order.periodAmount} {order.priceUnit === 'days' ? 'дней' : 'часов'}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  )}

                  {/* Информация о филиале (аренда) */}
                  {order.orderType !== 'shop' && order.branchSnapshot && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#757575', mb: 0.5 }}>
                        Пункт выдачи:
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#000000' }}>
                        {order.branchSnapshot.city && `${order.branchSnapshot.city}, `}
                        {order.branchSnapshot.address}
                      </Typography>
                    </Box>
                  )}

                  {order.orderType !== 'shop' && <Divider sx={{ my: 2 }} />}

                  {/* Сумма заказа (аренда) */}
                  {order.orderType !== 'shop' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: '#757575' }}>
                        Сумма аренды:
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        {order.totalPrice || 0} ₽
                      </Typography>
                    </Box>
                    {getDepositValue(order) > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#757575' }}>
                          Залог:
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#8B7D6B', fontWeight: 'bold' }}>
                          {getDepositValue(order)} ₽
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  )}

                  {/* Кнопка для заказов в статусе "Завершено" (аренда) */}
                  {order.orderType !== 'shop' && order.status === 'completed' && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Button
                        variant="contained"
                        onClick={() => handleRepeatOrder(order)}
                        disabled={processing}
                        sx={{
                          backgroundColor: '#8B7D6B',
                          color: '#FFFFFF',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#6B5D4B',
                          },
                        }}
                      >
                        Повторить заказ
                      </Button>
                    </Box>
                  )}

                  {/* Кнопки для заказов в статусе ожидания оплаты (аренда) */}
                  {order.orderType !== 'shop' && order.status === 'waiting_payment' && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      {(() => {
                        const timeLeft = getTimeUntilCancel(order);
                        return (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Button
                                variant="contained"
                                onClick={() => handlePayPrepayment(order)}
                                disabled={processing || !timeLeft}
                                sx={{
                                  backgroundColor: '#8B7D6B',
                                  color: '#FFFFFF',
                                  fontWeight: 'bold',
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: '#6B5D4B',
                                  },
                                  '&:disabled': {
                                    backgroundColor: '#8B7D6B',
                                    opacity: 0.6,
                                  },
                                }}
                              >
                                Внести предоплату
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  setSelectedOrderId(order._id);
                                  setError('');
                                  setCancelDialogOpen(true);
                                }}
                                disabled={processing}
                                sx={{
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  textTransform: 'none',
                                  '&:hover': {
                                    borderColor: '#b71c1c',
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                  },
                                }}
                              >
                                Отменить
                              </Button>
                            </Box>
                        );
                      })()}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Диалог оплаты */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => !processing && setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#F5F3F0',
          },
        }}
      >
        <DialogTitle sx={{ color: '#000000', fontWeight: 'bold', fontFamily: 'serif' }}>
          Подтверждение бронирования
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {selectedOrderId && (() => {
            const order = orders.find(o => o._id === selectedOrderId);
            if (!order) return null;
            
            const prepaidAmount = getPrepaidAmount(order);
            const paymentDetails = order.branchSnapshot?.paymentDetails;
            
            return (
              <Box>
                {/* Сумма предоплаты */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#FFFFFF', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold', mb: 1 }}>
                    Сумма к оплате:
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#8B7D6B', fontWeight: 'bold' }}>
                    {prepaidAmount} ₽
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', mt: 1 }}>
                    (стоимость аренды за один день)
                  </Typography>
                </Box>

                {/* Реквизиты для оплаты */}
                {paymentDetails && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#FFFFFF', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ color: '#000000', fontWeight: 'bold', mb: 2 }}>
                      Реквизиты для оплаты:
                    </Typography>
                    {paymentDetails.bankName && (
                      <Typography variant="body1" sx={{ color: '#000000', mb: 1 }}>
                        <strong>Банк:</strong> {paymentDetails.bankName}
                      </Typography>
                    )}
                    {paymentDetails.recipientName && (
                      <Typography variant="body1" sx={{ color: '#000000', mb: 1 }}>
                        <strong>Получатель:</strong> {paymentDetails.recipientName}
                      </Typography>
                    )}
                    {paymentDetails.phoneNumberForPayment && (
                      <Typography variant="body1" sx={{ color: '#000000', mb: 1 }}>
                        <strong>Телефон для оплаты:</strong> {paymentDetails.phoneNumberForPayment}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Комментарий к платежу */}
                <Box>
                  <Typography variant="body1" sx={{ color: '#000000', mb: 2 }}>
                    Если вы производите оплату с чужой карты, пожалуйста, укажите комментарии: от кого и из какого банка отправлен платеж.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Комментарий к платежу (необязательно)"
                    value={paymentComment}
                    onChange={(e) => setPaymentComment(e.target.value)}
                    disabled={processing}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#8B7D6B',
                        },
                        '&:hover fieldset': {
                          borderColor: '#6B5D4B',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8B7D6B',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8B7D6B',
                      },
                    }}
                  />
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentDialogOpen(false);
              setSelectedOrderId(null);
              setPaymentComment('');
              setError('');
            }}
            disabled={processing}
            sx={{
              color: '#8B7D6B',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleConfirmPayment}
            disabled={processing}
            sx={{
              backgroundColor: '#8B7D6B',
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#6B5D4B',
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            {processing ? 'Обработка...' : 'Я внес предоплату'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения предоплаты по туру */}
      <Dialog
        open={tourPaymentDialogOpen}
        onClose={() => !processing && setTourPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#F5F3F0' },
        }}
      >
        <DialogTitle sx={{ color: '#000000', fontWeight: 'bold', fontFamily: 'serif' }}>
          Подтверждение предоплаты по туру
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: '#000000', mb: 2 }}>
            Если перевод был с чужой карты, укажите комментарий: кто отправитель и из какого банка.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Комментарий к платежу (необязательно)"
            value={tourPaymentComment}
            onChange={(e) => setTourPaymentComment(e.target.value)}
            disabled={processing}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '& fieldset': { borderColor: '#8B7D6B' },
                '&:hover fieldset': { borderColor: '#6B5D4B' },
                '&.Mui-focused fieldset': { borderColor: '#8B7D6B' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#8B7D6B' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTourPaymentDialogOpen(false);
              setSelectedOrderId(null);
              setTourPaymentComment('');
              setError('');
            }}
            disabled={processing}
            sx={{
              color: '#8B7D6B',
              '&:hover': { backgroundColor: 'rgba(139, 125, 107, 0.1)' },
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleConfirmTourPayment}
            disabled={processing}
            sx={{
              backgroundColor: '#8B7D6B',
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#6B5D4B' },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {processing ? 'Обработка...' : 'Я внес предоплату'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог отмены */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => !processing && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#F5F3F0',
          },
        }}
      >
        <DialogTitle sx={{ color: '#000000', fontWeight: 'bold', fontFamily: 'serif' }}>
          Отмена заказа
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body1" sx={{ color: '#000000' }}>
            Вы уверены, что хотите отменить этот заказ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setSelectedOrderId(null);
              setError('');
            }}
            disabled={processing}
            sx={{
              color: '#8B7D6B',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Нет
          </Button>
          <Button
            onClick={handleCancelOrder}
            disabled={processing}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            {processing ? 'Обработка...' : 'Да, отменить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrdersPage;

